import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axiosInstance from '../../services/api'
import {
  GET_BILTIES,
  GET_BILTY,
  POST_GENERATE_BILTY_PDF,
  GET_DOWNLOAD_BILTY_PDF,
  POST_APPROVE_BILTY
} from '../../helpers/url_helper'

// Async Thunks

/**
 * Fetch all bilties with filters and pagination
 * @param {Object} params - Query parameters (page, limit, contract_id, etc.)
 */
export const fetchBilties = createAsyncThunk(
  'bilty/fetchBilties',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams(params).toString()
      const response = await axiosInstance.get(`${GET_BILTIES}?${queryString}`)
      return response.data
    } catch (error) {
      console.error('Fetch bilties error:', error)
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch bilties'
      )
    }
  }
)

/**
 * Fetch single bilty by ID
 * @param {string} id - Bilty ID
 */
export const fetchBiltyById = createAsyncThunk(
  'bilty/fetchBiltyById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${GET_BILTY}/${id}`)
      return response.data
    } catch (error) {
      console.error('Fetch bilty details error:', error)
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch bilty details'
      )
    }
  }
)

/**
 * Generate PDF for a bilty
 * @param {string} id - Bilty ID
 */
export const generateBiltyPDF = createAsyncThunk(
  'bilty/generatePDF',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${POST_GENERATE_BILTY_PDF}/${id}/generate-pdf`)
      return response.data
    } catch (error) {
      console.error('Generate PDF error:', error)
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to generate PDF'
      )
    }
  }
)

/**
 * Download PDF for a bilty
 * @param {string} id - Bilty ID
 */
export const downloadBiltyPDF = createAsyncThunk(
  'bilty/downloadPDF',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${GET_DOWNLOAD_BILTY_PDF}/${id}/download-pdf`, {
        responseType: 'blob'
      })
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition']
      let filename = `bilty-${id}.pdf`
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
        if (filenameMatch) filename = filenameMatch[1]
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      return { success: true, message: 'PDF downloaded successfully' }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to download PDF'
      )
    }
  }
)

/**
 * Approve online bilty (Transporter action)
 * @param {string} id - Bilty ID
 */
export const approveBilty = createAsyncThunk(
  'bilty/approve',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${POST_APPROVE_BILTY}/${id}/approve`)
      return response.data
    } catch (error) {
      console.error('Approve bilty error:', error)
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to approve bilty'
      )
    }
  }
)

// Initial State
const initialState = {
  bilties: [],
  currentBilty: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  filters: {
    page: 1,
    limit: 10,
    contract_id: '',
    loading_id: '',
    transport_request_id: '',
    nomination_id: '',
    transporter_id: '',
    truck_number: '',
    bilty_type: '',
    bilty_source: '',
    transporter_approved: ''
  },
  loading: false,
  error: null,
  pdfGenerating: false,
  pdfDownloading: false,
  approving: false,
  successMessage: null
}

// Slice
const biltySlice = createSlice({
  name: 'bilty',
  initialState,
  reducers: {
    // Set filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    
    // Reset filters
    resetFilters: (state) => {
      state.filters = initialState.filters
    },
    
    // Clear current bilty
    clearCurrentBilty: (state) => {
      state.currentBilty = null
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null
    },
    
    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null
    }
  },
  extraReducers: (builder) => {
    // Fetch Bilties
    builder
      .addCase(fetchBilties.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBilties.fulfilled, (state, action) => {
        state.loading = false
        state.bilties = action.payload.data || []
        state.pagination = {
          currentPage: action.payload.pagination?.page || 1,
          totalPages: action.payload.pagination?.totalPages || 1,
          totalItems: action.payload.pagination?.total || 0,
          itemsPerPage: action.payload.pagination?.limit || 10
        }
      })
      .addCase(fetchBilties.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Fetch Single Bilty
    builder
      .addCase(fetchBiltyById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBiltyById.fulfilled, (state, action) => {
        state.loading = false
        state.currentBilty = action.payload.data
      })
      .addCase(fetchBiltyById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Generate PDF
    builder
      .addCase(generateBiltyPDF.pending, (state) => {
        state.pdfGenerating = true
        state.error = null
      })
      .addCase(generateBiltyPDF.fulfilled, (state, action) => {
        state.pdfGenerating = false
        state.successMessage = action.payload.message
        // Update current bilty with PDF info
        if (state.currentBilty) {
          state.currentBilty.pdf_url = action.payload.data.pdf_url
          state.currentBilty.pdf_generated_at = action.payload.data.pdf_generated_at
        }
      })
      .addCase(generateBiltyPDF.rejected, (state, action) => {
        state.pdfGenerating = false
        state.error = action.payload
      })

    // Download PDF
    builder
      .addCase(downloadBiltyPDF.pending, (state) => {
        state.pdfDownloading = true
        state.error = null
      })
      .addCase(downloadBiltyPDF.fulfilled, (state, action) => {
        state.pdfDownloading = false
        state.successMessage = action.payload.message
      })
      .addCase(downloadBiltyPDF.rejected, (state, action) => {
        state.pdfDownloading = false
        state.error = action.payload
      })

    // Approve Bilty
    builder
      .addCase(approveBilty.pending, (state) => {
        state.approving = true
        state.error = null
      })
      .addCase(approveBilty.fulfilled, (state, action) => {
        state.approving = false
        state.successMessage = action.payload.message
        // Update current bilty approval status
        if (state.currentBilty) {
          state.currentBilty.transporter_approved = true
          state.currentBilty.approved_at = action.payload.data.approved_at
          state.currentBilty.approved_by = action.payload.data.approved_by
        }
        // Update in list if present
        const biltyIndex = state.bilties.findIndex(b => b.id === state.currentBilty?.id)
        if (biltyIndex !== -1) {
          state.bilties[biltyIndex].transporter_approved = true
        }
      })
      .addCase(approveBilty.rejected, (state, action) => {
        state.approving = false
        state.error = action.payload
      })
  }
})

// Export actions
export const {
  setFilters,
  resetFilters,
  clearCurrentBilty,
  clearError,
  clearSuccessMessage
} = biltySlice.actions

// Export reducer
export default biltySlice.reducer
